/**
 * TenantDashboard — pulpit OSOBISTY tenanta (iter. 1 + Faza 23 Etap 2).
 *
 * Cienki kontener: ładuje układ usera (`useDashboardLayout` → resolver serwerowy osobisty →
 * domyślny tenanta → null), przy `null` stosuje wbudowany `buildDefaultLayout(t)` (bez zapisu,
 * dopóki user nie wejdzie w edycję i nie kliknie Zapisz). Cała maszyneria edycji jest we wspólnym
 * `<DashboardEditor>` — zapis idzie do wiersza OSOBISTEGO (adopt-and-customize).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DashboardEditor } from '@/components/dashboard/DashboardEditor';
import { buildDefaultLayout } from '@/components/dashboard/default-layout';
import { useDashboardLayout } from '@/components/dashboard/useDashboardLayout';
import { Spinner } from '@/components/Spinner';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useActiveCompanyScope } from '@/lib/use-active-company';

export function TenantDashboard() {
  const { t } = useTranslation();
  const { me, loading: meLoading } = useTenantAuth();
  const { isAll, companyId } = useActiveCompanyScope();
  const {
    layout: remoteLayout,
    source: layoutSource,
    isLoading: layoutLoading,
    saveLayout,
    isSaving,
    resetLayout,
  } = useDashboardLayout();

  const firstName = useMemo(() => {
    const display = me?.user.displayName;
    if (display) {
      const first = display.trim().split(/\s+/)[0];
      return first || display;
    }
    const email = me?.user.email ?? '';
    const local = email.split('@')[0];
    return local || '';
  }, [me]);

  const defaultLayout = useMemo(() => buildDefaultLayout(t), [t]);
  const baseLayout = remoteLayout ?? defaultLayout;

  // Powitanie wg pory dnia (od 18:00 „Dobry wieczór") i podtytuł „Przegląd firmy X · miesiąc rok".
  const greetingKey =
    new Date().getHours() >= 18
      ? 'tenant.dashboard.header.greetingEvening'
      : 'tenant.dashboard.header.greetingMorning';
  const month = new Date().toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
  const activeCompanyName = me?.companies.find((c) => c.id === companyId)?.name;
  const subtitle =
    isAll || !activeCompanyName
      ? t('tenant.dashboard.header.subtitleAll', { month })
      : t('tenant.dashboard.header.subtitleCompany', { company: activeCompanyName, month });

  if ((meLoading && !me) || layoutLoading) {
    return (
      <main className="min-h-full bg-bg-secondary p-6">
        <div className="mx-auto flex max-w-5xl items-center justify-center py-20">
          <Spinner />
        </div>
      </main>
    );
  }

  if (!me) {
    return (
      <main className="min-h-full bg-bg-secondary p-6">
        <div className="w-full py-20 text-center text-text-tertiary">
          {t('tenant.dashboard.notLoaded')}
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-full bg-bg-secondary p-6" data-testid="tenant-dashboard">
      <DashboardEditor
        baseLayout={baseLayout}
        onSave={(next) => saveLayout(next)}
        isSaving={isSaving}
        showReportTools
        onReset={resetLayout}
        canReset={layoutSource === 'personal'}
        headerContent={
          <div>
            <h1 className="text-2xl font-bold text-text-primary">
              {t(greetingKey, { name: firstName })}
            </h1>
            <p className="mt-1 text-sm text-text-tertiary">{subtitle}</p>
          </div>
        }
      />
    </main>
  );
}
